import { Sites } from '@ps/platform-message-framework/common/Sites';
import { Cookie as CookieNames, FeatureFlag } from '@ps/web-core-enums';
import type { AccountPayload } from '@ps/web-core-store';
import { LoginSource, useFeature } from '@ps/web-core-store';
import { DebugKey, canUseDOM, createDebug } from '@ps/web-core-utils';
import { format, formatISO } from 'date-fns';
import { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { Unsubscribe } from 'redux';
import { Subject, lastValueFrom } from 'rxjs';
import Cookies from 'universal-cookie';

import { Country } from '@swf/core/enums';
import useRequest from '@swf/core/hooks/useRequest';
import idpLogin from '@swf/core/models/auth/idpLogin';
import { registrationRequest } from '@swf/core/models/auth/register';
import { fetchAccountDetails, subscribe } from '@swf/core/models/auth/utils';
import { fetchJWT } from '@swf/core/models/auth/verifySession';
import type { Dispatch, RootState, Store } from '@swf/core/store';
import { dispatch, select } from '@swf/core/store';
import type { BoundRequest } from '@swf/core/types/Request';
import type { WizardContext } from '@swf/core/types/Wizard';

import useSetMitIdData from '../../../hooks/useSetMitIdData';

const cookies = new Cookies();

const AUTH_TIMEOUT = 60000;

const DEFAULT_COUNTRY = Country.UnitedStates;

const debug = createDebug(DebugKey.Auth);

const mobileAuthenticate = (store: Store, userId: string, password: string) =>
  new Promise<boolean>((resolve, reject) => {
    let unsubscribe: Unsubscribe | null = null;

    const timeout = setTimeout(() => {
      const error = new Error(
        `Timed out: isClientAuthenticated did not become true within ${AUTH_TIMEOUT} seconds`
      );
      reject(error);
      unsubscribe?.();
      debug(error.message);
    }, AUTH_TIMEOUT);

    unsubscribe = store.subscribe(() => {
      const state = store.getState();

      if (select.session.isClientAuthenticated(state)) {
        resolve(true);
        clearTimeout(timeout);
        unsubscribe?.();
      }
    });
    canUseDOM && window?.starsweb?.auth?.passAuthCredentials(userId, password);
  });

const useSignupServices = () => {
  const usedRequest = useRequest();
  const store = useStore<RootState>() as Store;
  const site = useSelector(select.cms.getSiteId);
  const isPhone = useSelector(select.device.isPhone);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const { account } = useDispatch<Dispatch>();
  const blacklistCheck = useFeature(FeatureFlag.BlackListCheck);
  const setMitIdData = useSetMitIdData();

  const request: BoundRequest = useCallback(
    (options) => lastValueFrom(usedRequest(options)),
    [usedRequest]
  );

  const setCountry = useCallback(({ values }: WizardContext) => {
    if (!canUseDOM) {
      values.country = DEFAULT_COUNTRY;
      return Promise.resolve({});
    }
    const queryParams = new URLSearchParams(document.location.search);
    const country = queryParams.get('country');
    const region = queryParams.get('state');
    if (country && region) {
      values.country = country;
      values.state = region;
    } else values.country = DEFAULT_COUNTRY;
    return Promise.resolve({});
  }, []);

  const setCAONProvince = useCallback(
    ({ values }: WizardContext) => {
      if (site === Sites.CA_ON) {
        values.country = 'CA';
        values.state = 'ON';
      }
      return Promise.resolve({});
    },
    [site]
  );

  const signupCountryCheck = useCallback(
    async ({ values: { country } }: { values: { country: string } }) => {
      if (!country) return Promise.reject({});
      if (
        (site === Sites.POR && country !== Country.Portugal) ||
        (site === Sites.GR && country !== Country.Greece) ||
        (site === Sites.DEN && country !== Country.Denmark && country !== Country.Greenland) ||
        (site === Sites.BUL && country !== Country.Bulgaria) ||
        (site === Sites.ROM && country !== Country.Romania) ||
        (site === Sites.EST && country !== Country.Estonia)
      ) {
        return { isCountryAvailable: false };
      }
      const installId = cookies.get(CookieNames.INSTALL_ID);
      const { body: isCountryAvailable } = await request({
        service: '/api/v1-preview/registration/country/validate',
        method: 'POST',
        http: true,
        body: { installId, country }
      });

      if (country === Country.Canada && (site === Sites.CA_ON || site === Sites.COM)) {
        const redirectionTag = isPhone && site === Sites.COM ? 'caon_mob_redirect' : 'caon_signup';
        const { body: redirectUrl } = await request({
          service: 'tags.resolve',
          body: { tag: redirectionTag }
        });

        return { isCountryAvailable, redirectUrl };
      }
      return { isCountryAvailable };
    },

    [isPhone, request, site]
  );

  const signupRegister = useCallback(
    async ({ values }: Pick<WizardContext, 'values'>) => {
      const loginObservable = new Subject<boolean>();
      const { dob, password, userId, ...payload } = values;
      if (blacklistCheck && dob) {
        payload.dob = formatISO(values.dob);
      }

      const { body } = await registrationRequest(
        request,
        { password, userId, ...payload, skipLogin: isMobileEmbedded },
        store.getState()
      );

      const isIdpSecondaryLoginEnabled = select.feature.check(
        store.getState(),
        FeatureFlag.IdpSecondaryLoginEnabled
      );

      if (isIdpSecondaryLoginEnabled) {
        await idpLogin({ password, userId }, { dispatch, rootState: store.getState() });
      }

      if (isMobileEmbedded) {
        await mobileAuthenticate(store, userId, password);
      } else {
        const token = await fetchJWT(
          {
            token: body.token,
            signature: body.signature,
            webId: body.userWebId
          },
          request
        );

        await fetchAccountDetails(
          { token, loginSource: LoginSource.Registration },
          { dispatch, rootState: store.getState() }
        );
      }

      const isSwcSignupConsentPreferences = select.feature.check(
        store.getState(),
        FeatureFlag.SwcSignupConsentPreferences
      );

      await subscribe(dispatch, loginObservable);

      let updateAccountPayload: AccountPayload = {};
      if (!blacklistCheck && values.dob) {
        updateAccountPayload = { dob: format(values.dob, 'yyyy-LL-dd') };
      }

      if (isSwcSignupConsentPreferences) {
        updateAccountPayload = {
          ...updateAccountPayload,
          mailListPoker: values.mailListPoker ?? false,
          mailListCasino: values.mailListCasino ?? false,
          mailListSports: values.mailListSports ?? false,
          communicationChannelEmail: values.communicationChannelEmail ?? false,
          communicationChannelPushNotification: values.communicationChannelPushNotification ?? false
        };
      }

      if (Object.keys(updateAccountPayload).length > 0) {
        updateAccountPayload = { ...updateAccountPayload, fetchAccount: true };
        await account.updateAccount(updateAccountPayload);
      }

      return { signupPassed: true };
    },
    [blacklistCheck, request, store, isMobileEmbedded, account]
  );

  const signupResultContext = () => setMitIdData;

  return {
    setCountry,
    setCAONProvince,
    signupCountryCheck,
    signupRegister,
    signupResultContext
  };
};

export default useSignupServices;
